<i18n>
{
    "uk": {
        "defaultReviewsTitle": "Оцінка курсу та відгуки студентів",
        "reviewsAverageCount": "Немає середньої оцінки | Середня оцінка за відгуками {n} студента | Середня оцінка за відгуками {n} студентів | Середня оцінка за відгуками {n} студентів",
        "fullReview": "Повний відгук"
    },
    "en": {
        "defaultReviewsTitle": "Course rating and student feedback",
        "reviewsAverageCount": "No average rating | Average rating based on feedback from {n} student | Average rating based on reviews of {n} students",
        "fullReview": "Full review"
    }
}
</i18n>


<template>
    <div
        v-if="course.rating && course.rating.length > 0"
        :id="block.id || `${block.type}-${uid}`"
        class="mt-6 mt-md-8 mt-lg-10"
    >
        <div class="page-container">
            <h2 class="ta-center h4 h3-sm h2-md fw-800 fw-sm-700">
                {{ (block.title) || $t('defaultReviewsTitle', locale) }}
            </h2>
        </div>

        <SideScroll class="d-flex flex-nowrap reviews-ribbon" ref="sidescroll">

            <Card :lower="true" class="reviews-average scroll-item d-flex flex-column justify-content-center align-items-center px-6">
                <div class="f-small color-gray-0 mb-1">
                    {{ $tc('reviewsAverageCount', course.rating.length, locale) }}
                </div>

                <div class="reviews-average-score">
                    {{ course.rating.average.toLocaleString().split('.').join(',') }}
                </div>

                <div class="reviews-average-meter-inner mt-2">
                    <div :style="{
                        width: `${course.rating.average * 20}%`
                    }" class="reviews-average-stars color-yellow-2">
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                    </div>
                    <div class="reviews-average-bg color-gray-3">
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                        <span class="icon-ic-star" />
                    </div>
                </div>
            </Card>


            <Card
                v-for="(review, i) in reviewsToShow"
                :key="`reviews-item-${i}`"
                class="reviews-item scroll-item"
            >
                <div class="reviews-item-inner">

                    <component
                        :is="review.user.slug ? 'nuxt-link' : 'div'"
                        :to="localePath(`/${review.user.slug}`)"
                        target="_blank"
                        class="reviews-author-photo"
                        v-profile="{
                            modelId: review.user.id,
                            modelName: 'user',
                            placement: 'right'
                        }"
                    >
                        <WebpPicture
                            :src="review.user.photo"
                            :alt="review.user.displayName"
                            imgClass="image-block"
                            width="64"
                            height="64"
                            loading="lazy"
                        />
                    </component>
                    <component
                        :is="review.user.slug ? 'nuxt-link' : 'div'"
                        :to="localePath(`/${review.user.slug}`)"
                        target="_blank"
                        class="f-small fw-700 d-inline-block"
                        :class="{
                            'd-link': !!review.user.slug
                        }"
                        v-profile="{
                            modelId: review.user.id,
                            modelName: 'user',
                            placement: 'right'
                        }"
                    >
                        {{ review.user.displayName }}
                    </component>
                    <div class="f-small color-gray-2" v-if="review.createdAt">
                        {{ $timeformat['dd.mm.yyyy'](review.createdAt) }}
                    </div>


                    <p v-if="review.shortComment" class="mt-3">
                        {{ review.shortComment }}

                        <panel-button
                            class="b-link"
                            name="reviews-full-comment"
                            @click.native="currentReview = review"
                        >
                            {{ $t('fullReview') }}
                        </panel-button>
                    </p>
                    <p v-else class="mt-3">{{ review.comment }}</p>


                    <div class="reviews-item-rating bg-gray-5">
                        <span class="reviews-rating-icon color-yellow-2 icon-ic-star" />
                        <span>{{ review.rating }}</span>
                    </div>
                </div>
            </Card>

            <!-- Skeleton -->
            <div id="review-observer"/>
            <Card
                class="reviews-item scroll-item"
                v-if="reviewsToShow.length < reviewsLength"
            />
            <Card
                class="reviews-item scroll-item"
                v-if="reviewsToShow.length < reviewsLength"
            />
            <Card
                class="reviews-item scroll-item"
                v-if="reviewsToShow.length < reviewsLength"
            />
        </SideScroll>

        <CasesPanel
            ref="motion"
            modal-name="reviews-full-comment"
            side="right"
            defTitle="Повний відгук"
        >
            <div class="p-2">
                <div class="reviews-item-inner">
                    <component
                        v-if="currentReview.user"
                        :is="currentReview.user.slug ? 'nuxt-link' : 'div'"
                        :to="localePath(`/${currentReview.user.slug}`)"
                        target="_blank"
                        class="reviews-author-photo"
                        v-profile="{
                            modelId: currentReview.user.id,
                            modelName: 'user',
                            placement: 'left'
                        }"
                    >
                        <WebpPicture
                            :src="currentReview.user.photo"
                            :alt="currentReview.user.displayName"
                            imgClass="image-block"
                            width="64"
                            height="64"
                            loading="lazy"
                        />
                    </component>
                    <component
                        v-if="currentReview.user"
                        :is="currentReview.user.slug ? 'nuxt-link' : 'div'"
                        :to="localePath(`/${currentReview.user.slug}`)"
                        target="_blank"
                        class="f-small fw-700 d-inline-block"
                        :class="{
                            'd-link': !!currentReview.user.slug
                        }"
                        v-profile="{
                            modelId: currentReview.user.id,
                            modelName: 'user',
                            placement: 'left'
                        }"
                    >
                        {{ currentReview.user.displayName }}
                    </component>

                    <div class="f-small color-muted" v-if="currentReview.createdAt">
                        {{ $timeformat['dd.mm.yyyy'](currentReview.createdAt)}}
                    </div>

                    <div class="mt-3 mb-n2">
                        <p class="ws-pre-wrap">{{ currentReview.comment }}</p>
                    </div>

                    <div class="reviews-item-rating bg-gray-5">
                        <span class="reviews-rating-icon color-yellow-2 icon-ic-star" />
                        <span>{{ currentReview.rating }}</span>
                    </div>
                </div>
            </div>
        </CasesPanel>
    </div>
</template>


<script>
    import Card        from '~/components/cards/Card.vue';
    import CasesPanel  from '~/components/panels/CasesPanel.vue';
    import PanelButton from '~/components/panels/PanelButton.vue';
    import SideScroll  from '~/components/SideScroll.vue';
    import WebpPicture  from'~/components/WebpPicture.vue';

    import { useCourseLandingStore } from '~/stores/courseLanding';

    let uid = 0;

    export default {
        props: {
            block: Object,
        },

        components: {
            Card,
            CasesPanel,
            PanelButton,
            SideScroll,
            WebpPicture
        },

        data () {
            uid++;

            return {
                currentReview: {},
                reviewsToShow: [],
                reviewsLength: 0,
                loading: false,

                uid
            };
        },

        computed: {
            locale () {
                return this.courseLandingStore.locale;
            },

            course () {
                return this.courseLandingStore.course;
            },

            reviews () {
                return this.courseLandingStore.reviews;
            },

            courseLandingStore () {
                return useCourseLandingStore();
            },
        },

        created () {
            this.addReviews(this.reviews.rates || [])
            this.reviewsLength = this.reviews.length || 0
        },

        mounted () {
            this.observer = new IntersectionObserver(([entry]) => {
                if (entry && entry.isIntersecting ) {
                    this.loadReviews();
                }
            }, {
                root:       null,
                rootMargin: '0px',
                threshold:  0.1
            });

            let el = document.getElementById('review-observer');
            if (el) this.observer.observe(el);
        },

        methods: {
            addReviews (raws) {
                const limit = 140;

                raws.forEach(review => {
                    if (!review.comment) return;

                    // Trim review
                    if (review.comment.length > limit) {
                        let sentences  = review.comment.split('.');
                        let cutComment = '';

                        for (let i = 0, l = sentences.length; i < l; i++) {
                            cutComment += (i > 0 ? '.' : '') + sentences[i];

                            if (cutComment.length >= limit) break;
                        }

                        if (cutComment.length > limit) {
                            let words  = cutComment.split(' ');
                            cutComment = '';

                            for (let i = 0, l = words.length; i < l; i++) {
                                cutComment += (i > 0 ? ' ' : '') + words[i];

                                if (cutComment.length >= limit) break;
                            }

                            cutComment += '...';
                        }

                        review.shortComment = cutComment;
                    }

                    this.reviewsToShow.push(review);
                });

                this.$nextTick(() => {
                    if (this.$refs && this.$refs.sidescroll) {
                        this.$refs.sidescroll.refresh();
                    }
                })
            },

            loadReviews () {
                if (this.reviewsToShow.length >= this.reviewsLength || this.loading) return;
                this.loading = true;

                this.$axios.$get('/rating/getreviews', {
                    params: {
                        query: {
                            where: {
                                comment: {
                                    $exists: true,
                                    $ne: ''
                                },
                                modelName: 'course',
                                modelId: this.course.id
                            },
                            limit: 25,
                            sort: 'createdAt DESC',
                            skip: this.reviewsToShow.length
                        }
                    },
                    process: false
                })
                .then(res => {
                    this.reviewsLength = res.length || 0;
                    this.addReviews(res.rates || []);
                    this.loading = false;
                })
                .catch(err => {
                    console.log(err);
                    this.loading = false;
                })
            }
        },

        destroyed () {
            if (this.observer) this.observer.disconnect();
        }
    };
</script>


<style lang="postcss" scoped>
    .reviews-ribbon {
        :deep(.icon-ic-arrow-simple) {
            top: 50%;
        }
    }

    .reviews-average,
    .reviews-item {
        white-space: normal;

        margin: 0 12px;

        flex-shrink: 0;

        &:first-child {
            margin-left: 0;
        }

        &:last-child {
            margin-right: 0;
        }
    }

    .reviews-average {
        text-align: center;

        width: 240px;
    }

    .reviews-average-score {
        font-size:   96px;
        font-weight: 300;
        line-height: 1;
    }

    .reviews-average-meter {
        display: inline-block;
    }

    .reviews-average-meter-inner {
        position: relative;
    }

    .reviews-average-stars,
    .reviews-average-bg {
        font-size: 20px;

        display: flex;

        .icon-ic-star {
            margin: 0 2px;
        }
    }

    .reviews-average-stars {
        width: 0;

        position: absolute;
        overflow: hidden;
        top:      0;
        left:     0;
    }

    .reviews-item {
        width: 328px;
    }

    .reviews-item-inner {
        position: relative;
    }

    .reviews-author-photo {
        display: block;
        margin-bottom: 8px;
        border-radius: 50%;

        width:    64px;
        height:   64px;
        overflow: hidden;

        box-shadow: 0px 0px 2px 0px rgba(9, 10, 13, 0.24);

        img {
            display: block;
        }
    }

    .reviews-item-rating {
        border-radius: 99999px;
        padding:       0 4px;

        display:     flex;
        align-items: center;
        min-width:   42px;

        position: absolute;
        top:      0;
        right:    0;
    }

    .reviews-rating-icon {
        font-size:   12px;
        line-height: 16px;

        margin-left:  2px;
        margin-right: 6px;
    }
</style>
